import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Crown, MoveRight } from "lucide-react";
import { IMAGES } from "@/lib/pricing";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pb-28 pt-32 lg:pt-40" data-testid="hero-section">
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="absolute -right-40 top-10 -z-10 h-[480px] w-[480px] rounded-full bg-gradient-to-br from-[#2A5C43] via-[#A8B89A] to-[#D87D92] opacity-25 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.34em] text-[#2A5C43]">
              Custom Apparel Atelier
            </span>
          </motion.div>

          <h1 className="ff-serif text-[17vw] font-light leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl" data-testid="hero-heading">
            <MaskedLine delay={0.45}>Makie&rsquo;s</MaskedLine>
            <MaskedLine delay={0.58} className="italic">
              Majestic
            </MaskedLine>
            <MaskedLine delay={0.71} className="ff-script text-brand-gradient pb-4 pr-4 text-[15vw] font-semibold sm:text-8xl lg:text-9xl">
              Kreations
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="ff-script mt-6 text-2xl text-[#B08A2E] sm:text-3xl"
            data-testid="hero-motto"
          >
            — Crafting Dreams, One Creation at a Time
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-6 max-w-md text-base font-light leading-relaxed text-[#1A2421]/70"
          >
            Custom tees, sweatshirts, and hoodies made to order — your design, your color,
            finished by hand and shipped to your door.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#order")}
              className="group flex items-center gap-3 rounded-full bg-[#1A2421] px-8 py-4 text-xs uppercase tracking-[0.24em] text-[#FDFBF7] transition-colors hover:bg-[#2A5C43]"
              data-testid="hero-order-cta"
            >
              Start Your Order
              <MoveRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#collection")}
              className="rounded-full border border-[#1A2421]/25 px-8 py-4 text-xs uppercase tracking-[0.24em] text-[#1A2421] transition-colors hover:border-[#D4AF37] hover:text-[#2A5C43]"
              data-testid="hero-collection-cta"
            >
              The Collection
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-12 flex items-center gap-3 text-[11px] uppercase tracking-[0.26em] text-[#1A2421]/50"
          >
            <span>3 signature pieces</span>
            <span className="h-1 w-1 rotate-45 bg-[#D4AF37]" />
            <span>8 sizes</span>
            <span className="h-1 w-1 rotate-45 bg-[#D4AF37]" />
            <span>any color, any design</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-t-[999px] border border-[#E5D8D3] shadow-[0_30px_90px_rgba(42,92,67,0.22)]">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.hero}
                alt="A pristine custom tee waiting for its design"
                className="h-[440px] w-full scale-110 object-cover sm:h-[560px]"
                data-testid="hero-image"
              />
              <div className="pointer-events-none absolute inset-0 rounded-t-[999px] bg-gradient-to-t from-[#2A5C43]/25 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-10 -left-6 flex h-32 w-32 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#FDFBF7] shadow-[0_16px_50px_rgba(212,175,55,0.25)] sm:-left-10 sm:h-36 sm:w-36"
            >
              <svg viewBox="0 0 120 120" className="animate-spin-slow absolute inset-0 h-full w-full" aria-hidden="true">
                <defs>
                  <path id="mmk-circle" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0" />
                </defs>
                <text className="fill-[#1A2421]" style={{ fontSize: "8.6px", letterSpacing: "2.6px" }}>
                  <textPath href="#mmk-circle">MAKIE&rsquo;S MAJESTIC KREATIONS · CRAFTING DREAMS ·</textPath>
                </text>
              </svg>
              <Crown size={26} className="text-[#B08A2E]" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -right-4 bottom-16 hidden w-36 rotate-3 overflow-hidden rounded-sm border-4 border-[#FDFBF7] shadow-[0_20px_60px_rgba(26,36,33,0.25)] sm:block lg:-right-8"
            >
              <img src={IMAGES.craft} alt="Hand-finishing a custom piece" className="h-40 w-full object-cover" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("#collection")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-[#1A2421]/40 transition-colors hover:text-[#B08A2E] lg:block"
        aria-label="Scroll to the collection"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
